import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { toStorySlug } from '../../../lib/slugify';
import { useAuth } from '../../../hooks/useAuth';

interface Story {
  id: string;
  title: string;
  genre: string;
  content: string;
  cover_image: string | null;
  like_count: number;
  read_count: number;
  published_date: string;
  profiles: {
    display_name: string;
  };
}

interface StoryStats {
  likeCount: number;
  commentCount: number;
  avgRating: number | null;
  liked: boolean;
}

const TopRankedCard = ({ story, index }: { story: Story; index: number }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<StoryStats>({
    likeCount: 0,
    commentCount: 0,
    avgRating: null,
    liked: false,
  });
  const [likeLoading, setLikeLoading] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      const [{ count: likes }, { count: comments }, { data: ratingData }] = await Promise.all([
        supabase.from('story_likes').select('id', { count: 'exact', head: true }).eq('story_id', story.id),
        supabase.from('comments').select('id', { count: 'exact', head: true }).eq('story_id', story.id),
        supabase.rpc('get_story_avg_rating', { p_story_id: story.id }),
      ]);

      const avg =
        ratingData && ratingData.length > 0 && ratingData[0].rating_count > 0
          ? Number(ratingData[0].avg_rating)
          : null;

      setStats((prev) => ({
        ...prev,
        likeCount: likes ?? 0,
        commentCount: comments ?? 0,
        avgRating: avg,
      }));
    };

    fetchStats();
  }, [story.id]);

  useEffect(() => {
    const checkLiked = async () => {
      if (!user) { setStats((prev) => ({ ...prev, liked: false })); return; }
      const { data } = await supabase
        .from('story_likes')
        .select('id')
        .eq('user_id', user.id)
        .eq('story_id', story.id)
        .maybeSingle();
      setStats((prev) => ({ ...prev, liked: !!data }));
    };
    checkLiked();
  }, [user, story.id]);

  const handleLike = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { navigate('/auth/login'); return; }
    if (likeLoading) return;
    setLikeLoading(true);
    try {
      if (stats.liked) {
        await supabase.from('story_likes').delete().eq('user_id', user.id).eq('story_id', story.id);
        setStats((prev) => ({ ...prev, liked: false, likeCount: Math.max(0, prev.likeCount - 1) }));
      } else {
        await supabase.from('story_likes').insert({ user_id: user.id, story_id: story.id });
        setStats((prev) => ({ ...prev, liked: true, likeCount: prev.likeCount + 1 }));
      }
    } catch (err) {
      console.error('Error toggling like:', err);
    } finally {
      setLikeLoading(false);
    }
  };

  const getExcerpt = (content: string) => {
    const plainText = content.replace(/<[^>]*>/g, '');
    return plainText.length > 120 ? plainText.slice(0, 120) + '...' : plainText;
  };

  const getReadTime = (content: string) => {
    const words = content.replace(/<[^>]*>/g, '').split(/\s+/).length;
    return `${Math.ceil(words / 200)} min read`;
  };

  const getTags = (genre: string) => {
    const tagMap: Record<string, string[]> = {
      'Psychological Horror': ['psychological', 'mind-bending'],
      'Supernatural': ['supernatural', 'paranormal'],
      'Cosmic Dread': ['cosmic', 'lovecraftian'],
      'Urban Legends': ['urban', 'folklore'],
      'Slasher': ['slasher', 'thriller'],
      'Body Horror': ['body horror', 'grotesque'],
      'Gothic': ['gothic', 'dark'],
    };
    return tagMap[genre] || ['horror', 'dark'];
  };

  return (
    <Link
      to={`/story/${toStorySlug(story.title, story.id)}`}
      className="group relative bg-[#0f0f0f] border border-red-900/20 rounded-lg overflow-hidden hover:border-red-600/50 transition-all duration-300 hover:shadow-2xl hover:shadow-red-900/30 cursor-pointer flex flex-col"
      style={{ animation: `slideInUp 0.6s ease-out ${index * 0.1}s both` }}
    >
      {/* Rank Badge */}
      <div className="absolute top-4 left-4 z-10 w-12 h-12 flex items-center justify-center bg-red-600 text-white font-bold text-xl rounded-full shadow-lg font-['Cinzel']">
        #{index + 1}
      </div>

      {/* Image */}
      <div className="relative h-48 overflow-hidden shrink-0">
        <img
          src={story.cover_image || `https://public.readdy.ai/ai/img_res/1bc20eb6b9b358940f78a7b23459c9ce.jpg${story.id}&orientation=landscape`}
          alt={story.title}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0f0f0f] via-transparent to-transparent" />
      </div>

      {/* Content */}
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-red-500 font-['Crimson_Text'] bg-red-600/10 px-2 py-1 rounded whitespace-nowrap">
            {story.genre}
          </span>
          <span className="text-xs text-gray-400 font-['Crimson_Text']">
            <i className="ri-time-line mr-1" />
            {getReadTime(story.content)}
          </span>
        </div>

        <h3 className="text-lg font-bold text-white mb-2 font-['Cinzel'] group-hover:text-red-500 transition-colors line-clamp-2">
          {story.title}
        </h3>

        <p className="text-gray-400 text-xs mb-3 font-['Crimson_Text']">
          by <span className="text-red-500">{story.profiles.display_name}</span>
        </p>

        <p className="text-gray-300 text-sm mb-4 font-['Crimson_Text'] leading-relaxed line-clamp-2 flex-1">
          {getExcerpt(story.content)}
        </p>

        {/* Tags */}
        <div className="flex flex-wrap gap-1 mb-4">
          {getTags(story.genre).slice(0, 2).map((tag) => (
            <span key={tag} className="text-xs text-gray-400 bg-black/40 px-2 py-1 rounded-md font-['Crimson_Text']">
              {tag}
            </span>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-red-900/20">
          <div className="flex items-center gap-4">
            <button
              onClick={handleLike}
              className={`flex items-center gap-1 transition-colors cursor-pointer ${
                stats.liked ? 'text-red-500' : 'text-gray-500 hover:text-red-500'
              }`}
            >
              <i className={stats.liked ? 'ri-heart-fill' : 'ri-heart-line'} />
              <span className="text-sm font-['Crimson_Text']">{stats.likeCount.toLocaleString()}</span>
            </button>
            <span className="flex items-center gap-1 text-gray-500 text-sm font-['Crimson_Text']">
              <i className="ri-chat-3-line" />
              {stats.commentCount.toLocaleString()}
            </span>
            {stats.avgRating !== null && (
              <span className="flex items-center gap-1 text-yellow-500 text-sm font-['Crimson_Text']">
                <i className="ri-star-fill" />
                {stats.avgRating.toFixed(1)}
              </span>
            )}
          </div>
          <i className="ri-arrow-right-line text-red-500 group-hover:translate-x-2 transition-transform" />
        </div>
      </div>
    </Link>
  );
};

const TopRanked = () => {
  const [stories, setStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStories = async () => {
      try {
        const { data, error } = await supabase
          .from('stories')
          .select(`
            id, title, genre, content, cover_image, like_count, read_count, published_date,
            profiles!stories_author_id_fkey (display_name)
          `)
          .eq('status', 'published')
          .order('read_count', { ascending: false })
          .limit(6);
        if (error) throw error;
        setStories(data || []);
      } catch (error) {
        console.error('Error fetching top ranked stories:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStories();
  }, []);

  if (loading) {
    return (
      <section className="py-20 px-4 bg-gradient-to-b from-[#0a0a0a] to-[#1a0a0a]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-red-600 mb-4 font-['Cinzel']">Most Terrifying Tales</h2>
            <p className="text-gray-400 text-lg font-['Crimson_Text']">The stories that kept our community awake</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-[#0f0f0f] border border-red-900/20 rounded-lg overflow-hidden animate-pulse">
                <div className="h-48 bg-gray-800" />
                <div className="p-5 space-y-3">
                  <div className="h-5 bg-gray-800 rounded w-3/4" />
                  <div className="h-4 bg-gray-800 rounded w-1/2" />
                  <div className="h-4 bg-gray-800 rounded" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (stories.length === 0) {
    return (
      <section className="py-20 px-4 bg-gradient-to-b from-[#0a0a0a] to-[#1a0a0a]">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-red-600 mb-4 font-['Cinzel']">Most Terrifying Tales</h2>
          <p className="text-gray-400 text-lg font-['Crimson_Text']">No stories available yet</p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 px-4 bg-gradient-to-b from-[#0a0a0a] to-[#1a0a0a]">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-red-600 mb-4 font-['Cinzel']">Most Terrifying Tales</h2>
          <p className="text-gray-400 text-lg font-['Crimson_Text']">The stories that kept our community awake</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {stories.map((story, index) => (
            <TopRankedCard key={story.id} story={story} index={index} />
          ))}
        </div>

        <div className="text-center mt-12">
          <Link
            to="/stories"
            className="inline-block px-8 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-all duration-300 shadow-lg shadow-red-900/30 hover:shadow-red-900/50 font-['Crimson_Text'] whitespace-nowrap cursor-pointer"
          >
            View Full Rankings
          </Link>
        </div>
      </div>

      <style>{`
        @keyframes slideInUp {
          from { opacity: 0; transform: translateY(40px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
};

export default TopRanked;